// Game state machine.
//
// States:
//   LOADING — asset loader is running. No render yet (UI shows splash + bar).
//   MENU    — assets ready, waiting for the player to click "begin".
//   PLAYING — physics ticking, input affects the world.
//   PAUSED  — physics frozen, render still alive, input limited to unpause.
//
// Transitions are explicit methods (loadingDone / start / togglePause).
// Downstream code subscribes to `stateChange` to react (e.g. UI overlay
// showing/hiding, audio ducking, render-vs-update gating).

export const GameState = {
	LOADING: 'loading',
	MENU:    'menu',
	PLAYING: 'playing',
	PAUSED:  'paused',
};

export class Game {
	constructor(initialState = GameState.LOADING) {
		this.state = initialState;
		this._listeners = { stateChange: [] };
	}

	on(event, callback) {
		if (!this._listeners[event]) {
			throw new Error(`Game: unknown event "${event}"`);
		}
		this._listeners[event].push(callback);
		return this;
	}

	// ─── Transitions ─────────────────────────────────────────────

	loadingDone() {
		if (this.state === GameState.LOADING) this._setState(GameState.MENU);
	}

	start() {
		if (this.state === GameState.MENU) this._setState(GameState.PLAYING);
	}

	togglePause() {
		if (this.state === GameState.PLAYING) this._setState(GameState.PAUSED);
		else if (this.state === GameState.PAUSED) this._setState(GameState.PLAYING);
	}

	// ─── Predicates ──────────────────────────────────────────────

	get isPlaying() { return this.state === GameState.PLAYING; }
	get isPaused()  { return this.state === GameState.PAUSED; }
	get isMenu()    { return this.state === GameState.MENU; }
	get isLoading() { return this.state === GameState.LOADING; }

	// ─── Internal ────────────────────────────────────────────────

	_setState(next) {
		if (this.state === next) return;
		const prev = this.state;
		this.state = next;
		for (const cb of this._listeners.stateChange) cb(next, prev);
	}
}
